import { env } from '../env';
import type { Evidence } from '../types/case';

interface Props {
  evidence: Evidence[];
  /** Dashboard session token — uploaded files stream through the authed
      API, so plain anchors can't carry credentials. */
  token?: string;
}

const evidenceIcon = (type: string): string => {
  if (type.includes('ledger') || type.includes('payment')) return '📊';
  if (type.includes('letter') || type.includes('document')) return '📄';
  if (type.includes('photo') || type.includes('image')) return '📷';
  if (type.includes('communication') || type.includes('message')) return '💬';
  return '📎';
};

/** Fetch the authed file and open it in a new tab (anchor href can't
    attach the bearer token). Same pattern as FilesPanel: no features
    string on window.open (a features string makes Chrome treat it as a
    blockable popup) and a download-click fallback when blocked. */
async function openEvidenceFile(evidenceId: string, filename: string, token: string): Promise<void> {
  await openAuthedBlob(`${env.resolutionEngineBaseUrl}/evidence/${encodeURIComponent(evidenceId)}/file`, filename, token);
}

/** Evidence rows minted by the engine (archived signed agreements) carry
    RELATIVE /api/v1/... urls — the documents bucket is private, so bytes
    stream through the authed API. A raw <a> resolved them against the
    dashboard origin and just reloaded the SPA (found live 2026-08-24). */
export async function openEngineEvidenceUrl(contentUrl: string, filename: string, token: string): Promise<void> {
  const path = contentUrl.replace(/^\/api\/v1/, '');
  await openAuthedBlob(`${env.resolutionEngineBaseUrl}${path}`, filename, token);
}

async function openAuthedBlob(url: string, filename: string, token: string): Promise<void> {
  const resp = await fetch(url, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    window.alert('Could not open the document.');
    return;
  }
  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  const win = window.open(blobUrl, '_blank');
  if (!win) {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export function EvidenceList({ evidence, token }: Props) {
  if (evidence.length === 0) {
    return (
      <section className="card p-4">
        <div className="section-label">Key evidence</div>
        <p className="mt-2 text-sm text-gray-500">No evidence attached.</p>
      </section>
    );
  }

  return (
    <section className="card p-4">
      <div className="section-label">Key evidence</div>
      <ul className="mt-2 space-y-3 text-sm">
        {evidence.map((e) => {
          const meta = (e.metadata ?? {}) as Record<string, unknown>;
          const isUpload = e.source === 'upload';
          const label = (meta.filename as string) ?? e.description ?? e.content_summary ?? e.type;
          const analysis = (meta.analysis ?? null) as { doc_kind?: string; key_facts?: string[]; relevance?: string } | null;
          const status = meta.analysis_status as string | undefined;
          return (
            <li key={e.id} className="flex items-start gap-2">
              <span aria-hidden>{evidenceIcon(analysis?.doc_kind ?? e.type)}</span>
              <div className="flex-1">
                {isUpload && token ? (
                  <button
                    type="button"
                    onClick={() => void openEvidenceFile(e.id, label, token)}
                    className="font-medium text-brand hover:underline"
                  >
                    {label}
                  </button>
                ) : e.content_url && e.content_url.startsWith('/api/') && token ? (
                  <button
                    type="button"
                    onClick={() => void openEngineEvidenceUrl(e.content_url!, label, token)}
                    className="font-medium text-brand hover:underline"
                  >
                    {label}
                  </button>
                ) : e.content_url ? (
                  <a href={e.content_url} target="_blank" rel="noreferrer" className="font-medium text-brand hover:underline">
                    {label}
                  </a>
                ) : (
                  <span className="font-medium">{label}</span>
                )}
                <div className="text-xs text-gray-500">
                  {analysis?.doc_kind ? <span className="mr-2 rounded-full bg-surface-muted px-1.5 py-0.5">{analysis.doc_kind}</span> : e.source && <span>{e.source}</span>}
                  {e.verified && <span className="ml-1 text-green-700">Verified ✓</span>}
                  {status === 'pending' && <span className="ml-1 text-amber-700">analyzing…</span>}
                  {status === 'failed' && <span className="ml-1 text-amber-700">analysis unavailable</span>}
                </div>
                {e.content_summary && <p className="mt-0.5 text-xs text-gray-600">{e.content_summary}</p>}
                {analysis?.key_facts && analysis.key_facts.length > 0 && (
                  <ul className="mt-1 list-disc pl-4 text-xs text-gray-600">
                    {analysis.key_facts.slice(0, 4).map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
